import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useAuth } from 'shared/providers/auth';
import theme from 'shared/styles/theme';

import { VerifyIdentityErrorScreenEnum } from './VerifyIdentityErrorScreenEnum';

const VerifyIdentityErrorScreen = () => {
  const router = useRouter();
  const { t: translatedTextVerifyIdentity } = useTranslation('verifyIdentity');
  const { screenType } = useLocalSearchParams<{
    screenType?: VerifyIdentityErrorScreenEnum;
  }>();

  const { handleLogout } = useAuth();

  const goToLogin = () => {
    void handleLogout({ shouldNavigateToLogin: true });
  };

  useBackHandler(goToLogin);

  const getScreenContent = () => {
    switch (screenType) {
      case VerifyIdentityErrorScreenEnum.TooManyRequests:
        return {
          headline: translatedTextVerifyIdentity('tooManyRequests.header'),
          body: translatedTextVerifyIdentity('tooManyRequests.body'),
          button: {
            title: translatedTextVerifyIdentity('buttons.goToLogin'),
            onPress: goToLogin,
            analytics: {
              analyticsId: 'goToLogin',
              customAttributes: {
                error_type: 'too_many_requests',
              },
            },
          },
          screenCustomAttributes: {
            error_type: 'too_many_requests',
          },
        };
      case VerifyIdentityErrorScreenEnum.LinkExpired:
        return {
          headline: translatedTextVerifyIdentity('linkExpired.header'),
          body: translatedTextVerifyIdentity('linkExpired.body'),
          button: {
            title: translatedTextVerifyIdentity('buttons.goToLogin'),
            onPress: goToLogin,
            analytics: {
              analyticsId: 'goToLogin',
              customAttributes: {
                error_type: 'link_expired',
              },
            },
          },
          screenCustomAttributes: {
            error_type: 'link_expired',
          },
        };
      default:
        return {
          headline: translatedTextVerifyIdentity('unableToProcess.header'),
          body: translatedTextVerifyIdentity('unableToProcess.body'),
          button: {
            title: translatedTextVerifyIdentity('buttons.contactUs'),
            onPress: () => {
              router.push('/help');
            },
            analytics: {
              analyticsId: 'contactUs',
              customAttributes: {
                error_type: 'default',
              },
            },
          },
          secondaryButton: {
            title: translatedTextVerifyIdentity('buttons.done'),
            onPress: goToLogin,
            analytics: {
              analyticsId: 'goToLogin',
              customAttributes: {
                error_type: 'default',
              },
            },
          },
          screenCustomAttributes: {
            error_type: 'default',
          },
        };
    }
  };

  const { headline, body, button, secondaryButton, screenCustomAttributes } =
    getScreenContent();

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <ErrorScreen
        messageHeadline={headline}
        messageBody={body}
        primaryButton={button}
        secondaryButton={secondaryButton}
        screenType={ErrorScreenType.Error}
        screenAnalyticsEventKey="/login/verify-identity-error"
        customAttributes={screenCustomAttributes}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: '100%',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
});

export default VerifyIdentityErrorScreen;
